/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
// eslint-disable-next-line no-restricted-imports
import {
  deleteCookie as deleteCookieNext,
  setCookie as setCookieNext,
  getCookies as getCookiesNext,
} from 'cookies-next';
// eslint-disable-next-line no-restricted-imports
import type { CookieValueTypes, OptionsType } from 'cookies-next';
import type { CookieName } from '@/types/cookieTypes';
import { isServer } from '@/utils/isServer';

export type { CookieValueTypes, OptionsType };

type CookieOptions = OptionsType & {
  sameSite?: 'lax' | 'strict' | 'none';
  domain?: string;
};

/**
 * Set of universal cookie utility functions
 * Works in both server and client seamlessly
 */

export const deleteCookie = async (key: CookieName, options?: CookieOptions): Promise<void> => {
  const opts = applyCookieDefaults(options);
  if (isServer) {
    if (opts.req && opts.res) {
      await deleteCookieNext(key, opts);
      return;
    } else {
      throw new Error(
        `Cannot delete cookie on the server. req and/or res are missing for cookie - ${key}`,
      );
    }
  }
  await deleteCookieNext(key, opts);
};

export const setCookie = async (
  key: CookieName,
  value: CookieValueTypes,
  options?: CookieOptions,
): Promise<void> => {
  const opts = applyCookieDefaults(options);
  if (isServer) {
    if (opts?.req && opts?.res) {
      await setCookieNext(key, value, opts);
      return;
    } else {
      throw new Error(
        `Cannot set cookie on the server. req and/or res are missing for cookie - ${key}`,
      );
    }
  }
  await setCookieNext(key, value, opts);
};

/**
 * Note: Prefer to use `getDecodedCookies`
 *
 * This function provides backward compatibility by ensuring a synchronous return.
 * The new cookies-next API can return a Promise, but we handle this by returning
 * an empty object in those cases to maintain the existing API contract.
 */
export const getCookies = async (
  options?: CookieOptions,
): Promise<Record<string, string | undefined>> => {
  try {
    const cookies = getCookiesNext(options);

    if (cookies && typeof cookies === 'object' && 'then' in cookies) {
      const resolvedCookies = await cookies;
      return resolvedCookies as Record<string, string>;
    }

    if (!cookies) {
      return {};
    }

    return cookies as Record<string, string>;
  } catch (_error) {
    return {};
  }
};

/**
 * Server-side specific getCookies function that handles async behavior properly.
 * This should be used in server-side contexts where cookies are critical for authentication.
 */
export const getServerCookies = async (
  options?: CookieOptions,
): Promise<Record<string, string>> => {
  try {
    const cookies = getCookiesNext(options);

    if (cookies && typeof cookies === 'object' && 'then' in cookies) {
      const resolvedCookies = await cookies;
      return resolvedCookies as Record<string, string>;
    }

    if (!cookies) {
      return {};
    }

    return cookies as Record<string, string>;
  } catch (_error) {
    return {};
  }
};

type CookieGetter<T> = {
  get: (key: T) => string | undefined;
};

/**
 * Parse cookies (server and client side) and URL decode them.
 *
 * Important: the getter returns the values for the cookies
 * when they were first decoded, not at the moment of being called.
 */
export const getDecodedCookies = async <T extends CookieName>(
  keys: T[],
  options?: CookieOptions,
): Promise<CookieGetter<T>> => {
  if (isServer && !options?.req && !options?.res) {
    throw new Error('Cannot get cookies on the server. req and/or res are missing');
  }

  const cookies = getCookiesNext(options);
  const resolvedCookies =
    cookies && typeof cookies === 'object' && 'then' in cookies ? await cookies : cookies;

  const decodedCookies: Record<T, string | undefined> = keys.reduce(
    (newCookies, key) => {
      const value = resolvedCookies ? (resolvedCookies as Record<string, string>)[key] : undefined;

      newCookies[key] = value === undefined ? undefined : decode(value);

      return newCookies;
    },
    {} as Record<T, string | undefined>,
  );

  const get: CookieGetter<T>['get'] = (k: T) => {
    if (k in decodedCookies) {
      return decodedCookies[k];
    } else {
      throw new Error(
        `Trying to read value for cookie "${k}", but it wasn't decoded. Add it to the parameters of getDecodedCookies`,
      );
    }
  };

  return {
    get,
  };
};

/**
 * Synchronous version of getDecodedCookies for client-side use
 * This is a compromise for backward compatibility with client-side components
 */
export const getDecodedCookiesSync = <T extends CookieName>(
  keys: T[],
  options?: CookieOptions,
): CookieGetter<T> => {
  if (isServer && !options?.req && !options?.res) {
    throw new Error('Cannot get cookies on the server. req and/or res are missing');
  }

  const cookies = getCookiesNext(options);

  let resolvedCookies: Record<string, string> | undefined;

  if (cookies && typeof cookies === 'object') {
    if (!('then' in cookies)) {
      resolvedCookies = cookies as Record<string, string>;
    } else if (!isServer && typeof document !== 'undefined') {
      // Fallback: synchronous client parse
      resolvedCookies = document.cookie
        ? document.cookie.split(';').reduce(
            (acc, kv) => {
              const [k, ...rest] = kv.trim().split('=');
              if (k) {
                acc[k] = rest.join('=');
              }
              return acc;
            },
            {} as Record<string, string>,
          )
        : {};
    }
  } else if (!isServer && typeof document !== 'undefined') {
    // Handle case where cookies is falsy
    resolvedCookies = document.cookie
      ? document.cookie.split(';').reduce(
          (acc, kv) => {
            const [k, ...rest] = kv.trim().split('=');
            if (k) {
              acc[k] = rest.join('=');
            }
            return acc;
          },
          {} as Record<string, string>,
        )
      : {};
  }

  const decodedCookies: Record<T, string | undefined> = keys.reduce(
    (newCookies, key) => {
      const value = resolvedCookies ? resolvedCookies[key] : undefined;
      newCookies[key] = value === undefined ? undefined : decode(value);
      return newCookies;
    },
    {} as Record<T, string | undefined>,
  );

  const get: CookieGetter<T>['get'] = (k: T) => {
    if (k in decodedCookies) {
      return decodedCookies[k];
    } else {
      throw new Error(
        `Trying to read value for cookie "${k}", but it wasn't decoded. Add it to the parameters of getDecodedCookies`,
      );
    }
  };

  return {
    get,
  };
};

/**
 * Synchronous version of getCookies for client-side use
 * This is a compromise for backward compatibility with client-side components
 */
export const getCookiesSync = (options?: CookieOptions): Record<string, string> => {
  try {
    const cookies = getCookiesNext(options);

    if (cookies && typeof cookies === 'object' && !('then' in cookies)) {
      return cookies as Record<string, string>;
    }

    return {};
  } catch (_error) {
    return {};
  }
};

/**
 * Synchronous version of deleteCookie for client-side use
 * This is a compromise for backward compatibility with client-side components
 */
export const deleteCookieSync = (key: CookieName, options?: CookieOptions): void => {
  try {
    const opts = applyCookieDefaults(options);
    void deleteCookieNext(key, opts);
  } catch (_error) {
    // Silently fail for client-side compatibility
  }
};

const decode = (str: string): string => {
  if (!str) {
    return str;
  }

  return str.replace(/(%[0-9A-Z]{2})+/g, decodeURIComponent);
};

/**
 * Synchronous version of setCookie for client-side use
 * This is a compromise for backward compatibility with client-side components
 */
export const setCookieSync = (
  key: CookieName,
  value: CookieValueTypes,
  options?: CookieOptions,
): void => {
  const opts = applyCookieDefaults(options);

  if (isServer) {
    if (opts?.req && opts?.res) {
      // Server-side: use async setCookie
      void setCookie(key, value, opts);
      return;
    } else {
      throw new Error(
        `Cannot set cookie on the server. req and/or res are missing for cookie - ${key}`,
      );
    }
  }

  // Client-side: use synchronous cookies-next
  try {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setCookieNext(key, value, opts);
  } catch (error) {
    // Log error without console statement for production
    // eslint-disable-next-line no-console
    console.warn(`Failed to set cookie ${key}:`, error);
  }
};

const applyCookieDefaults = (options?: CookieOptions): CookieOptions => {
  const base: CookieOptions = { path: '/', ...(options ?? {}) };

  if (!('sameSite' in base) || base.sameSite === undefined) {
    base.sameSite = 'lax';
  }

  return base;
};

/**
 * Parses the utag_main cookie string and extracts the v_id value
 * This is a pure function that works on both client and server
 * @param utagMain - The utag_main cookie value
 * @returns The v_id value or empty string if not found
 */
export const parseVIdFromUtagMain = (utagMain: string): string => {
  if (!utagMain) {
    return '';
  }

  // Parse the $-delimited cookie value
  const parts = utagMain.split('$');
  const vIdPart = parts.find((part) => part.startsWith('v_id:'));

  if (!vIdPart) {
    return '';
  }

  // Extract the value after "v_id:"
  return vIdPart.substring(5) || ''; // "v_id:".length === 5
};

/**
 * Parses the utag_main cookie and extracts the v_id value
 * @returns The v_id value or empty string if not found
 */
export const getUtagMainVId = (): string => {
  if (isServer) {
    return '';
  }

  const cookies = getCookiesSync();
  const utagMain = cookies['utag_main'];
  const utagVisitorIdCookie = cookies['utag_main_v_id'];

  return parseVIdFromUtagMain(utagMain) || utagVisitorIdCookie || '';
};
